/**
 * Shared "not-found / sign-in" overlay for admin pages.
 *
 * The admin pages render their full panel skeleton unconditionally, then a
 * single `onMount` fetch decides whether to cover them with an authoritative
 * overlay (the worker returns 404 to non-admins; 401 collapses to a sign-in
 * prompt). The builder was duplicated in `signups-view.ts` and
 * `users-view.ts`; it now lives here. Callers pass pre-resolved i18n strings
 * so this module stays locale-agnostic.
 */
import type { AdminResult } from './admin-fetch.js';

export interface OverlayCopy {
  /** `data-testid` on the overlay element. */
  testid: string;
  notFoundHeading: string;
  notFoundBody: string;
  unauthHeading: string;
  unauthBody: string;
  unauthLink: string;
  /** Sign-in link `data-testid` (unauthenticated case). */
  signInTestid: string;
  /** CSS selector for the content block(s) to hide behind the overlay. */
  hideSelector: string;
  /** CSS selector of the intro node the overlay is inserted after. */
  introSelector: string;
}

/**
 * Render a single authoritative not-found / unauthenticated overlay over a
 * page's panels. Returns `true` when an overlay was rendered (so the caller
 * short-circuits its population path). No-op for `ok` results or non-gate
 * errors (those surface inline via the page's status node).
 */
export function maybeRenderOverlay(
  root: Element,
  result: AdminResult<unknown>,
  copy: OverlayCopy,
): boolean {
  if (result.ok) return false;
  if (result.kind !== 'not-found' && result.kind !== 'unauthenticated') return false;
  if (root.querySelector('[data-overlay-rendered]')) return true;

  const overlay = document.createElement('div');
  overlay.setAttribute('data-overlay-rendered', '');
  overlay.setAttribute('class', 'admin-llm-overlay');
  overlay.setAttribute('role', 'alert');
  overlay.setAttribute('data-testid', copy.testid);

  const heading = document.createElement('h2');
  const body = document.createElement('p');
  if (result.kind === 'not-found') {
    heading.textContent = copy.notFoundHeading;
    body.textContent = copy.notFoundBody;
    overlay.setAttribute('data-overlay-kind', 'not-found');
  } else {
    heading.textContent = copy.unauthHeading;
    body.textContent = copy.unauthBody;
    overlay.setAttribute('data-overlay-kind', 'unauthenticated');
    const a = document.createElement('a');
    a.setAttribute('href', '/sign-in');
    a.setAttribute('data-testid', copy.signInTestid);
    a.textContent = copy.unauthLink;
    body.appendChild(document.createTextNode(' '));
    body.appendChild(a);
  }
  overlay.appendChild(heading);
  overlay.appendChild(body);

  for (const panel of root.querySelectorAll<HTMLElement>(copy.hideSelector)) {
    panel.hidden = true;
  }
  const intro = root.querySelector(copy.introSelector);
  if (intro && intro.parentElement) {
    intro.parentElement.insertBefore(overlay, intro.nextSibling);
  } else {
    root.appendChild(overlay);
  }
  return true;
}
